<script lang="ts">
  import { onMount } from "svelte"
  import { createEventDispatcher } from "svelte"
  import { Button } from "$lib/components/ui/button"
  import { Checkbox } from "$lib/components/ui/checkbox"
  import { Input } from "$lib/components/ui/input"
  import { Label } from "$lib/components/ui/label"
  import { getParentUrl } from "../utils"
  import userStore, { userStoreActions } from "$lib/stores/userStore"
  import { statsStoreActions } from "$lib/stores/statsStore"
  import PhoneInput from "$lib/components/ui/phone-number/phone-input.svelte"
  import Turnstile from "$lib/components/Turnstile/Turnstile.svelte"
  import { encryptJson } from "$lib/utils/encryption"
  import { PUBLIC_HASH } from "$env/static/public"
  import { t } from "$lib/utils/i18n"
  import { freeEmailProviders } from "$lib/utils/freeEmailProviders"

  interface Props {
    action: any
    competition: any
    isSubmitting: any
    openTermsDialog: any
    isExample?: boolean
    infoCollection: any
    fraudSettings: any
    embed: boolean
  }

  let {
    action,
    competition,
    isSubmitting = $bindable(),
    openTermsDialog,
    isExample = false,
    infoCollection,
    fraudSettings,
    embed,
  }: Props = $props()

  const dispatch = createEventDispatcher()

  let name = $state("")
  let termsAccepted = $state(false)
  let email = $state("")
  let errorMessage = $state(null)
  let phoneNumber = $state("")
  let showPhoneError = $state(false)
  let showEmailError = $state(false)
  let showBusinessEmailError = $state(false)
  let firstName = $state("")
  let lastName = $state("")
  let dateOfBirth = $state("")
  let showAgeError = $state(false)
  let turnstileToken: string | null = $state(null)
  let ThumbmarkJS: any
  let shouldShowCaptcha = $state(fraudSettings?.enableCaptcha)

  let fingerprint = null
  let fingerprintData = null

  onMount(async () => {
    try {
      await import(
        "https://cdn.jsdelivr.net/npm/@thumbmarkjs/thumbmarkjs/dist/thumbmark.umd.js"
      )
      ThumbmarkJS = (window as any).ThumbmarkJS
      fingerprint = ThumbmarkJS ? await ThumbmarkJS.getFingerprint() : null
      fingerprintData = ThumbmarkJS
        ? await ThumbmarkJS.getFingerprintData()
        : null
      if (!fingerprint) {
        shouldShowCaptcha = true
      }
    } catch (error) {
      console.error("Error loading ThumbmarkJS:", error)
      shouldShowCaptcha = true
    }
  })

  async function logUserIn() {
    if (shouldShowCaptcha && !turnstileToken) {
      console.error("Please complete the Turnstile challenge")
      return
    }

    if (embed) {
      window.parent.postMessage(
        {
          type: "PROMOAMP_USER_LOGGED_IN",
          competition: {
            title: competition.title,
            slug: competition.slug,
          },
          user: {
            name,
            email,
          },
        },
        "*",
      )
    } else {
      // Fire off FB Pixel conversion
      // Fire off FB Pixel conversion
      if (window?.fbq && typeof window.fbq === "function") {
        window.fbq("trackCustom", "PromoAmp_SignUp")
      }
    }

    isSubmitting = true

    const ageInput = document.querySelector('input[name="dateOfBirth"]')
    const age = ageInput?.value || null
    const ageFormat = ageInput?.placeholder || "DD/MM/YYYY"

    let referredBy = new URLSearchParams(window.location.search).get("ref")

    if (embed) {
      try {
        const parentUrl = await getParentUrl()
        const url = new URL(parentUrl)
        referredBy = url.searchParams.get("ref")
      } catch (error) {
        console.error("Error getting parent URL params:", error)
      }
    }

    if (fingerprintData === null && !turnstileToken) {
      shouldShowCaptcha = true
      isSubmitting = false
      return
    }

    const { encrypted, iv } = await encryptJson(fingerprintData, PUBLIC_HASH)
    const userName =
      infoCollection.nameOption === "full-name"
        ? name
        : `${firstName} ${lastName}`
    const { encrypted: requestId } = await encryptJson(
      {
        userName: userName,
        userEmail: email,
        competitionId: competition.id,
        timestamp: new Date().toISOString(),
      },
      PUBLIC_HASH,
    )

    try {
      const response = await fetch("/api/competitions/create-or-update-entry", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          isLogin: true,
          userName,
          userFirstName:
            infoCollection.nameOption === "full-name" ? null : firstName,
          userLastName:
            infoCollection.nameOption === "full-name" ? null : lastName,
          userPhoneNumber: infoCollection?.phoneOption?.collectPhone
            ? phoneNumber
            : null,
          userEmail: email,
          userAge: age && ageFormat ? { age, format: ageFormat } : null,
          competitionId: competition.id,
          referredBy,
          fingerprint,
          encryption: encrypted,
          requestId,
          turnstileToken: shouldShowCaptcha ? turnstileToken : null,
        }),
      })

      if (!response.ok) {
        const data = await response.json()
        if (data.error) {
          errorMessage = data.error
        }
        throw new Error("Failed to log in")
      }

      const data = await response.json()
      const entryId = data.entryId

      // Fetch updated entry data from API endpoint
      const entryResponse = await fetch(`/api/user/get-entries`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          slug: competition.slug,
          userId: email,
        }),
      })
      if (!entryResponse.ok) throw new Error("Failed to fetch entry data")

      const entryData = await entryResponse.json()
      userStoreActions.setUser({
        name:
          infoCollection.nameOption === "full-name"
            ? name
            : `${firstName} ${lastName}`,
        email,
        userFirstName:
          infoCollection.nameOption === "full-name" ? null : firstName,
        userLastName:
          infoCollection.nameOption === "full-name" ? null : lastName,
        userPhoneNumber: infoCollection?.phoneOption?.collectPhone
          ? phoneNumber
          : null,
        userAge: age && ageFormat ? { age, format: ageFormat } : null,
        entryId,
        referredBy,
        completedActionIds: entryData.completedActionIds,
        completedActions: entryData.completedActions,
      })

      statsStoreActions.setStats({
        yourEntries: entryData.totalEntries,
        referralCount: entryData.referralCount,
        referrals: entryData.referrals,
      })

      if ($userStore?.user?.completedActionIds?.includes(action.id)) {
        userStoreActions.setOpenActionId(null)

        dispatch("close")
      } else {
        dispatch("close")
      }
    } catch (error) {
      console.error("Error during login:", error)
      // Handle error appropriately
    } finally {
      isSubmitting = false
    }
  }

  function isValidEmail(email: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  function isFreeEmailProvider(email: string) {
    const domain = email.split("@")[1]?.toLowerCase()
    return domain ? freeEmailProviders.includes(domain) : false
  }

  function calculateAge(birthDate: string) {
    const [day, month, year] = birthDate.split("/").map(Number)
    const today = new Date()
    const birth = new Date(year, month - 1, day)
    let age = today.getFullYear() - birth.getFullYear()
    const monthDiff = today.getMonth() - birth.getMonth()
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      age--
    }
    return age
  }

  function isValidDate(dateStr: string, ageFormat: string) {
    const formatRegex =
      ageFormat === "MM/DD/YYYY"
        ? /^(\d{2})\/(\d{2})\/(\d{4})$/
        : /^(\d{2})\/(\d{2})\/(\d{4})$/ // Assuming DD/MM/YYYY otherwise

    const match = dateStr.match(formatRegex)
    if (!match) return false

    let day: number, month: number, year: number
    if (ageFormat === "MM/DD/YYYY") {
      month = parseInt(match[1], 10)
      day = parseInt(match[2], 10)
      year = parseInt(match[3], 10)
    } else {
      // DD/MM/YYYY
      day = parseInt(match[1], 10)
      month = parseInt(match[2], 10)
      year = parseInt(match[3], 10)
    }

    const currentYear = new Date().getFullYear()

    // Check year bounds
    if (year < 1900 || year > currentYear) return false
    // Check month bounds (1-12)
    if (month < 1 || month > 12) return false
    // Check day bounds (initial check, refined later)
    if (day < 1 || day > 31) return false

    const date = new Date(year, month - 1, day)

    // Final check to ensure the date components match after Date object creation
    // This handles leap years and varying days in months correctly.
    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    )
  }

  function isValidPhone(phone: string) {
    if (!phone) return false
    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, "")
    // Check if we have at least 9 digits (common minimum for most countries)
    return digitsOnly.length >= 9 && digitsOnly.length <= 11
  }

  function handleSubmit() {
    if (infoCollection.showMinimumAge) {
      if (!isValidDate(dateOfBirth, infoCollection.ageFormat)) {
        showAgeError = true
      } else {
        const age = calculateAge(dateOfBirth)
        if (age < infoCollection.minimumAge) {
          showAgeError = true
        }
      }
    }

    if (!isValidEmail(email)) {
      showEmailError = true
    }

    // Check for business email requirement
    if (infoCollection?.requireBusinessEmail && isFreeEmailProvider(email)) {
      showBusinessEmailError = true
    }

    if (
      infoCollection?.phoneOption?.collectPhone &&
      infoCollection?.phoneOption?.isRequired &&
      !isValidPhone(phoneNumber)
    ) {
      showPhoneError = true
    }

    // Don't allow logging in if it's an example
    if (
      isExample ||
      showEmailError ||
      showAgeError ||
      showPhoneError ||
      showBusinessEmailError
    )
      return

    logUserIn()
  }

  function formatDateInput(event) {
    const input = event.target as HTMLInputElement
    let value = input.value.replace(/\D/g, "") // Remove non-digits

    if (value.length >= 2) {
      value = value.slice(0, 2) + "/" + value.slice(2)
    }
    if (value.length >= 5) {
      value = value.slice(0, 5) + "/" + value.slice(5)
    }

    // Limit to MM/DD/YYYY format length
    value = value.slice(0, 10)

    dateOfBirth = value
  }
</script>

<div class="space-y-2 mb-4 p-4 pb-0">
  {#if infoCollection.nameOption === "full-name"}
    <div class="flex items-center">
      <Label for="name" class="w-1/3">{t("widget_login_full_name")}</Label>
      <Input
        type="text"
        name="name"
        placeholder={t("widget_login_full_name")}
        class="w-2/3 text-base"
        bind:value={name}
      />
    </div>
  {:else}
    <div class="flex items-center">
      <Label for="firstName" class="w-1/3">{t("widget_login_first_name")}</Label
      >
      <Input
        type="text"
        name="firstName"
        placeholder={t("widget_login_first_name")}
        class="w-2/3 text-base"
        bind:value={firstName}
        required
      />
    </div>
    <div class="flex items-center">
      <Label for="lastName" class="w-1/3"
        >{t("widget_login_last_name")}
        {infoCollection.lastNameOptional ? t("widget_optional") : ""}</Label
      >
      <Input
        type="text"
        name="lastName"
        placeholder={t("widget_login_last_name")}
        class="w-2/3 text-base"
        bind:value={lastName}
        required={!infoCollection.lastNameOptional}
      />
    </div>
  {/if}

  {#if infoCollection?.phoneOption?.collectPhone}
    <div class="flex items-center">
      <Label for="phone" class="w-1/3">{t("widget_login_phone_number")}</Label>
      <div class="w-2/3">
        <PhoneInput
          name="phone"
          country={infoCollection?.phoneOption?.defaultCountry}
          placeholder={t("widget_login_phone_number_placeholder")}
          class="w-full text-base"
          bind:value={phoneNumber}
          onfocus={() => (showPhoneError = false)}
        />
        {#if showPhoneError}
          <p class="text-red-600 text-sm mt-1">
            {t("widget_login_invalid_phone")}
          </p>
        {/if}
      </div>
    </div>
  {/if}

  {#if infoCollection.showMinimumAge}
    <div class="flex items-center">
      <Label for="dateOfBirth" class="w-1/3"
        >{t("widget_login_date_of_birth")}</Label
      >
      <div class="w-2/3">
        <Input
          type="text"
          name="dateOfBirth"
          placeholder={infoCollection.ageFormat}
          class="w-full text-base pl-3"
          bind:value={dateOfBirth}
          oninput={formatDateInput}
          onfocus={() => (showAgeError = false)}
          maxlength="10"
        />
        {#if showAgeError}
          <p class="text-red-600 text-sm mt-1">
            <!-- TODO: Add this back in -->
            <!-- {infoCollection.showMinimumAge
              ? t("widget_login_age_restriction", { values: { age: infoCollection.minimumAge } })
              : t("widget_login_invalid_date")} -->
            You must be at least {infoCollection.minimumAge} years old. Make sure
            you enter your date of birth in this format: {infoCollection.ageFormat}
          </p>
        {/if}
      </div>
    </div>
  {/if}

  <div class="flex items-center">
    <Label for="email" class="w-1/3">{t("widget_login_email")}</Label>
    <div class="w-2/3">
      <Input
        type="email"
        name="email"
        placeholder={t("widget_login_email_placeholder")}
        class="w-full text-base"
        bind:value={email}
        onfocus={() => {
          showEmailError = false
          showBusinessEmailError = false
        }}
      />
      {#if showEmailError}
        <p class="text-red-600 text-sm mt-1">
          {t("widget_login_invalid_email")}
        </p>
      {/if}
      {#if showBusinessEmailError}
        <p class="text-red-600 text-sm mt-1">
          Please use a business email address. Personal email providers are not
          allowed.
        </p>
      {/if}
    </div>
  </div>

  {#if shouldShowCaptcha}
    <Turnstile bind:turnstileToken {isExample} />
  {/if}

  <div class="flex items-center !mt-4 !my-2">
    <Checkbox id="terms" class="mr-2" bind:checked={termsAccepted} />
    <Label for="terms" class="text-sm">
      {t("widget_login_terms_intro")}
      <button onclick={openTermsDialog} class="underline cursor-pointer inline">
        {t("widget_login_terms_link")}
      </button>
    </Label>
  </div>

  {#if errorMessage}
    <p class="text-red-600 text-sm mt-1">{@html errorMessage}</p>
  {/if}

  <Button
    variant="default"
    class="w-full bg-black text-white hover:bg-gray-800"
    onclick={handleSubmit}
    disabled={!termsAccepted ||
      isSubmitting ||
      (fraudSettings?.enableCaptcha && !turnstileToken)}
  >
    {#if isSubmitting}
      {t("widget_login_submitting")}
    {:else}
      {t("widget_login_submit")}
    {/if}
  </Button>
</div>
